import React, { useState, useCallback, Suspense } from 'react';
import { forTemplate } from '@trello/i18n';
import { TemplatesMenuTestIds } from '@trello/test-ids';
import { HeaderMenu } from 'app/src/components/HeaderMenu';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { useRecentlyUsedFeaturePreloader } from '@trello/recently-used-feature-preloader';
import { useTemplatePickerPreloader } from './useTemplatePickerPreloader';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';

const format = forTemplate('templates_menu');

const ErrorComponent: React.FC = () => (
  <p>{format('error-loading-templates')}</p>
);
interface ScreenToPush {
  screen: number | undefined;
  name: string;
}

export const TemplatesMenu: React.FC = () => {
  const [shouldHidePopover, setShouldHidePopover] = useState(false);
  const [skipPreload, setSkipPreload] = useState(true);
  const [screenToPush, setScreenToPush] = useState({
    screen: undefined,
    name: '',
  } as ScreenToPush);
  useTemplatePickerPreloader({ skip: skipPreload });

  const [
    TemplatesMenuPopovers,
    trackFeatureUsage,
  ] = useRecentlyUsedFeaturePreloader({
    featureName: 'TemplatesMenuPopovers',
    useLazyComponentArgs: [
      () =>
        import(
          /* webpackChunkName: "templates-menu-popovers" */ './TemplatesMenuPopovers'
        ),
      { namedImport: 'TemplatesMenuPopovers' },
    ],
    onPreloadedCallback: () => setSkipPreload(false),
  });

  const onHide = useCallback(() => {
    setShouldHidePopover(true);
  }, [setShouldHidePopover]);

  const onScreenPush = useCallback(
    ({ screen, name }: ScreenToPush) => {
      setScreenToPush({ screen, name });
    },
    [setScreenToPush],
  );

  return (
    <HeaderMenu
      buttonText={format('templates')}
      analyticsButtonName="templatesMenuButton"
      analyticsComponentName="templatesMenuInlineDialog"
      popoverTitle={format('templates')}
      dataTestId={TemplatesMenuTestIds.TemplatesMenu}
      pushScreen={screenToPush}
      shouldHidePopover={shouldHidePopover}
      onClick={trackFeatureUsage}
    >
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-teamplates',
          feature: 'Templates Menu',
        }}
        errorHandlerComponent={ErrorComponent}
      >
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={ErrorComponent}>
            <TemplatesMenuPopovers
              onHide={onHide}
              onScreenPush={onScreenPush}
            />
          </ChunkLoadErrorBoundary>
        </Suspense>
      </ErrorBoundary>
    </HeaderMenu>
  );
};
